/**
 * Edit the setup: a workspace that already exists, changed in the studio and
 * built again (step 9.16 of docs/plans/onboarding-harness/phase-09.md). This
 * file holds the shapes the server builds with.
 *
 *   SetupNow         the workspace as it stands: what an edit session is
 *                    seeded from (`draftFromWorkspace`) and what a build of it
 *                    is checked against.
 *   SetupChange      one change Build again makes: a record's fields changed,
 *                    a record added (with the list it needs), or a record left
 *                    out. The server applies the whole set in one transaction
 *                    or not at all (`applySetupChanges`).
 *
 * Nothing here writes a row; every rule about what may change is the
 * server's, and it answers with every problem at once.
 */
import type { RecordBlankCounts, RecordIconId, VisibleRecordKind } from './api';
import type { StudioLink, StudioTile } from './setup-draft/draft';

/* ------------------------------------------------------------ the workspace */

/** Which way a record moves money, as the outline words it; null on a list. */
export type SetupNowDirection = 'in' | 'out' | 'both' | 'count';

export type SetupNowField = {
  readonly id: string;
  readonly label: string;
  readonly type: string;
  /** The record a reference picks from. */
  readonly targetRecordId: string | null;
  readonly required: boolean;
};

export type SetupNowRecord = {
  readonly id: string;
  readonly name: string;
  readonly kind: VisibleRecordKind;
  readonly direction: SetupNowDirection | null;
  readonly icon: RecordIconId | null;
  /** The version a change to its fields is made on; a newer one refuses the change. */
  readonly fieldSetVer: number;
  /** Live entries (rows, on a list): what leaving it out keeps. */
  readonly entries: number;
  /** Its live fields, in form order. */
  readonly fields: readonly SetupNowField[];
};

export type SetupNowLink = {
  readonly id: string;
  readonly fromRecordId: string;
  readonly toRecordId: string;
  /**
   * Every field the link reads or fills, on either end: what its trigger
   * asks, what its map copies and writes, the rows it reads through. A field
   * here cannot be taken off while the link runs.
   */
  readonly fieldIds: readonly string[];
};

/** The workspace as it stands, as `loadSetupNow` reads it. */
export type SetupNow = {
  readonly tenantId: string;
  readonly name: string;
  readonly currency: string;
  readonly records: readonly SetupNowRecord[];
  readonly links: readonly SetupNowLink[];
};

/* ------------------------------------------------------------- the changes */

/** The kinds of field a change may add. A worked-out field is the record's own editor's. */
export const SETUP_FIELD_TYPES = [
  'text',
  'long_text',
  'number',
  'money',
  'quantity',
  'date',
  'datetime',
  'boolean',
  'select',
  'reference',
  'phone',
] as const;

export type SetupFieldType = (typeof SETUP_FIELD_TYPES)[number];

/**
 * A field a change adds. A reference picks from a record that is there
 * (`targetRecordId`) or from one this same set adds (`targetRecordKey`, the
 * add's `key`); never both.
 */
export type SetupFieldAdd = {
  readonly key: string;
  readonly label: string;
  readonly type: SetupFieldType;
  readonly required?: boolean;
  /** The choices of a select, in the person's words. */
  readonly options?: readonly string[];
  readonly targetRecordId?: string;
  readonly targetRecordKey?: string;
};

/** A field renamed. */
export type SetupFieldRename = { readonly fieldId: string; readonly label: string };

/**
 * A built field's own settings changed (step 9.16 of
 * docs/plans/onboarding-harness/phase-09.md): whether it must be filled, and
 * new choices on a select. Entries already kept are untouched.
 */
export type SetupFieldPatch = { readonly fieldId: string; readonly required?: boolean; readonly addOptions?: readonly string[] };

/** A record that is there (`recordId`) or one this same set adds (`recordKey`, the add's `key`). */
export type SetupRecordRef = { readonly recordId: string } | { readonly recordKey: string };

export type SetupChange =
  | {
      /** A record's own fields: added, taken off (archived, never deleted), renamed, patched. */
      readonly kind: 'change';
      readonly recordId: string;
      readonly fieldSetVer: number;
      readonly add?: readonly SetupFieldAdd[];
      readonly archive?: readonly string[];
      readonly rename?: readonly SetupFieldRename[];
      readonly patch?: readonly SetupFieldPatch[];
    }
  | {
      /** A record or a list that is not there yet. `key` names it to a reference in this set. */
      readonly kind: 'add';
      readonly key: string;
      readonly recordKind: VisibleRecordKind;
      readonly name: string;
      /** What each entry counts, on a record; absent on a list. */
      readonly counts?: RecordBlankCounts;
      readonly fields: readonly SetupFieldAdd[];
      /** A new list's first names (at most `SETUP_CHANGE_LIMITS.rows`), written as its first rows. */
      readonly rows?: readonly string[];
    }
  | {
      /** A record left out: archived with every entry it holds, never deleted. */
      readonly kind: 'leave_out';
      readonly recordId: string;
    };

/**
 * The changes Build again writes (9.16): every `SetupChange`, and the kinds
 * Edit the setup in the studio adds. The old changes route and its readers
 * keep to `SetupChange`; only an edit session's build writes these.
 */
export type SetupEditChange =
  | SetupChange
  | {
      /** A built record renamed: its entries stay; only the name changes. */
      readonly kind: 'rename_record';
      readonly recordId: string;
      readonly name: string;
    }
  | {
      /**
       * A new feed link, as the draft holds it (`link`, by draft keys), between
       * two records that are there or that this set adds. The build resolves
       * its field keys to ids once every record of the set stands.
       */
      readonly kind: 'add_link';
      readonly from: SetupRecordRef;
      readonly to: SetupRecordRef;
      readonly link: StudioLink;
      /**
       * The built link this one stands in for, when the draft moved a built
       * link's from or to (links-fixes L5): that one is retired in the same set
       * (`remove_link`), and the rows it wrote stay.
       */
      readonly replaces?: string;
    }
  | {
      /**
       * A built link's rule changed: its conditions, its fills, its sign, or
       * paused and on again, as the draft holds it (`link`, by draft keys). Its
       * from and to stay. The build writes it through `changeLink`, as the Links
       * page's change does: the version moves on, and the rows already written
       * keep the rule that wrote them (a change never posts earlier entries).
       */
      readonly kind: 'change_link';
      readonly linkId: string;
      readonly link: StudioLink;
    }
  | {
      /** A built link taken out: retired, and every row it wrote stays. */
      readonly kind: 'remove_link';
      readonly linkId: string;
    }
  | {
      /** A built link paused: it posts nothing until it is turned on again. */
      readonly kind: 'pause_link';
      readonly linkId: string;
    }
  | {
      /** A new card on top of a record, as the draft holds it (by field keys). */
      readonly kind: 'add_card';
      readonly record: SetupRecordRef;
      /** The draft record's key, so its field keys resolve. */
      readonly recordKey: string;
      readonly tile: StudioTile;
    }
  | {
      /** A built card taken off its record: `cardId` is `<summary view id>#<place>` (`draftFromWorkspace`'s `built`). */
      readonly kind: 'remove_card';
      readonly recordId: string;
      readonly cardId: string;
    };

/** One record the build touched, and how. */
export type SetupChangeMade = {
  readonly recordId: string;
  readonly name: string;
  readonly kind: VisibleRecordKind;
  readonly made: 'changed' | 'added' | 'left_out' | 'renamed';
};

/**
 * One reason the set was not built. `change` is its place in the set, and
 * `recordId`/`recordName` the record it is about (a record this set adds has
 * no id yet), so the outline marks it To fix.
 */
export type SetupChangeProblem = {
  readonly change: number;
  readonly code: string;
  readonly message: string;
  readonly recordId: string | null;
  readonly recordName: string;
};

/** The ceilings, generous for what a person says in one conversation. */
export const SETUP_CHANGE_LIMITS = {
  changes: 12,
  fields: 20,
  name: 60,
  label: 60,
  options: 30,
  key: 40,
  /** The first names a new list is given in Edit the setup (9.16), as a draft's seed rows. */
  rows: 24,
} as const;

/**
 * How many changes the person is asked to build. A list added only because a
 * record added beside it picks from it is part of that record's change
 * ("Home deliveries: new, with a new list, Riders"), so it is not counted on
 * its own.
 */
export const setupChangeCount = (changes: readonly SetupChange[]): number => {
  const pickedByAdd = new Set(
    changes.flatMap((change) =>
      change.kind === 'add' ? change.fields.flatMap((field) => (field.targetRecordKey === undefined ? [] : [field.targetRecordKey])) : [],
    ),
  );
  return changes.filter((change) => !(change.kind === 'add' && change.recordKind === 'directory' && pickedByAdd.has(change.key))).length;
};
